import { isEmpty, isNone, isPresent } from '@ember/utils';
import { computed, set } from '@ember/object';
import { alias, equal } from '@ember/object/computed';
import { attr, hasMany } from '@ember-data/model';
import { inject as service } from '@ember/service';
import Money from '@square/dashboard-money/models/money';
import NumericEnum from '@square/dashboard-utilities/utils/numeric-enum';
import Enum from '@square/dashboard-utilities/utils/enum';
import ItemsV3Base from '@square/dashboard-items-data/models/items-v3-base';
import PricingRule from '@square/dashboard-items-data/models/pricing-rule';
import { feature } from '@square/ember-features/macros';
const MAXIMUM_FRACTION_DIGITS = 2;
const DEFAULT_VARIABLE_DISCOUNT_VALUE = 0;
const DiscountType = NumericEnum.create(['FIXED', 'VARIABLE_PERCENTAGE', 'VARIABLE_AMOUNT']);
const ModifyTaxBasis = NumericEnum.create(['MODIFY_TAX_BASIS', 'DO_NOT_MODIFY_TAX_BASIS']);
const ApplicationMethod = NumericEnum.create(['MANUALLY_APPLIED', 'COMP']);
const DiscountRule = new Enum(['BUY_ONE_GET_ONE', 'EXACT_QUANTITY', 'MIN_QUANTITY']);
const PurchaseRule = new Enum(['QUANTITY', 'PRODUCTS', 'CUSTOMER']);

// Creates a Discount model object that will be defined to use Cogs API or new Merchant Catalog API
// depending upon how the user's merchant catalog feature flag is set
export default ItemsV3Base.extend({
  intl: service(),
  userInfo: service(),
  formatter: service(),
  store: service(),
  enableExcludeStrategy: feature('prices/enable_exclude_strategy'),
  price: attr('legacy-money'),
  amount: alias('price'),
  name: attr('string'),
  percentage: attr('number'),
  pinRequired: attr('boolean', {
    allowNull: true
  }),
  maximumAmount: attr('legacy-money'),
  // The Catalog API technically supports many promos being associated
  // with a single discount, but in practice we do not support such a
  // scenario and thus there is only ever a single promo
  promos: hasMany('promo'),
  promo: alias('promos.firstObject'),
  // The Catalog API technically supports many pricing rules being associated
  // with a single discount, but in practice we do not support such a
  // scenario and thus there is only ever a single pricing rule
  pricingRules: hasMany('pricing-rule', {
    autosaved: ['targetProductSet', 'adjustedProductSet', 'excludeProductSet', 'timePeriods', 'dateRangePeriod']
  }),
  pricingRule: alias('pricingRules.firstObject'),
  // There are 4 types of discounts:
  // - fixed flat amount (discount_type = FIXED, percentage = null, amount > 0)
  //   => The merchant enters an amount when editing the discount in the items library.
  // - fixed percentage (discount_type = FIXED, percentage > 0, amount = null)
  //   => The merchant enters a percentage when editing the discount in the items library.
  // - variable (aka custom) flat amount (discount_type = VARIABLE_AMOUNT, percentage = null, amount = 0)
  //   => The merchant enters an amount at sales time.
  // - variable (aka custom) percentage (discount_type = VARIABLE_PERCENTAGE, percentage = 0, amount = null)
  //   => The merchant enters a percentage at sales time.
  discountType: attr('enum', {
    enumClass: DiscountType,
    defaultValue: DiscountType.VARIABLE_PERCENTAGE
  }),
  calculationType: alias('discountType'),
  modifyTaxBasis: attr('enum', {
    enumClass: ModifyTaxBasis,
    defaultValue: ModifyTaxBasis.MODIFY_TAX_BASIS
  }),
  applicationMethod: attr('enum', {
    enumClass: ApplicationMethod,
    defaultValue: ApplicationMethod.MANUALLY_APPLIED
  }),
  isPercentageDiscount: computed('discountType', 'percentage', 'price.amount', {
    get() {
      const isVariablePercentageDiscount = this.discountType === DiscountType.VARIABLE_PERCENTAGE;

      // DiscountType of FIXED that is a percentage should not have a price value set
      const isFixedPercentageDiscount = this.discountType === DiscountType.FIXED && isNone(this.price?.amount);
      return isVariablePercentageDiscount || isFixedPercentageDiscount;
    },
    set(key, value) {
      if (value) {
        this.set('amountValue', undefined);
      } else {
        this.set('percentageValue', undefined);
      }
      return value;
    }
  }),
  isPercentage: alias('isPercentageDiscount'),
  inheritEnabledUnits() {
    this.pricingRules.filterBy('isDeleted', false).forEach(pricingRule => {
      pricingRule.inheritEnabledUnits(this);
    });
  },
  async save() {
    this.inheritEnabledUnits();
    const result = await this._super();
    return result;
  },
  isAutomatic: computed('_isAutomatic', 'pricingRule.deleted', 'pricingRules.length', {
    get() {
      if (this._isAutomatic !== undefined) {
        return this._isAutomatic;
      }
      return this.pricingRules.length > 0 && isPresent(this.pricingRule) && !this.pricingRule.deleted;
    },
    set(_, value) {
      return set(this, '_isAutomatic', value);
    }
  }),
  isFixedDiscount: computed('percentageValue', 'amountValue', function () {
    return !isNone(this.percentageValue) || !isNone(this.amountValue);
  }),
  isComp: equal('applicationMethod', ApplicationMethod.COMP),
  percentageValue: computed('percentage', 'discountType', {
    get() {
      if (this.discountType !== DiscountType.FIXED) {
        return null;
      }
      return this.percentage;
    },
    set(key, value) {
      this.set('percentage', value);
      return value;
    }
  }),
  amountValue: computed('amount.amount', 'discountType', {
    get() {
      if (this.discountType !== DiscountType.FIXED) {
        return null;
      }
      if (isEmpty(this.amount)) {
        return null;
      }
      return this.amount.amount;
    },
    set(key, value) {
      this.set('amount', Money.fromCents(value));
      const [oldPrice] = this.changedAttributes().price ?? [];

      // If new amount is the same as the original saved amount, then revert back
      // to use the original money object so that the isDirty flag can be reset.
      if (oldPrice?.amount === value) {
        this.set('amount', oldPrice);
      }
      return value;
    }
  }),
  amountType: computed('discountType', 'isPercentage', {
    get() {
      switch (this.discountType) {
        case DiscountType.VARIABLE_PERCENTAGE:
          return ItemsV3Base.AmountTypes.VARIABLE_PERCENTAGE;
        case DiscountType.VARIABLE_AMOUNT:
          return ItemsV3Base.AmountTypes.VARIABLE_AMOUNT;
        default:
          return this.isPercentage ? ItemsV3Base.AmountTypes.PERCENTAGE : ItemsV3Base.AmountTypes.AMOUNT;
      }
    },
    set(key, value) {
      let discountType;
      switch (value) {
        case ItemsV3Base.AmountTypes.VARIABLE_PERCENTAGE:
          discountType = DiscountType.VARIABLE_PERCENTAGE;
          break;
        case ItemsV3Base.AmountTypes.VARIABLE_AMOUNT:
          discountType = DiscountType.VARIABLE_AMOUNT;
          break;
        default:
          discountType = DiscountType.FIXED;
          break;
      }
      this.set('discountType', discountType);
      this.set('isPercentage', [ItemsV3Base.AmountTypes.VARIABLE_PERCENTAGE, ItemsV3Base.AmountTypes.PERCENTAGE].includes(value));
      return value;
    }
  }),
  discountText: computed('_discountText', 'amount.{amount,currencyCode}', 'discountType', 'intl', 'isPercentageDiscount', 'percentage', {
    get() {
      if (this._discountText !== undefined) {
        return this._discountText;
      }
      let currencyCode = this.amount?.currencyCode;
      if (isNone(currencyCode)) {
        currencyCode = Money.defaultCurrencyCode;
      }
      const currencyFormatter = this.formatter.getCurrencyFormatter(currencyCode);
      switch (this.discountType) {
        case DiscountType.VARIABLE_AMOUNT:
          return this.intl.t('items.discounts.variablePriceLabel', {
            currencySymbol: currencyFormatter.currencySymbol()
          });
        case DiscountType.VARIABLE_PERCENTAGE:
          return this.intl.t('items.discounts.variablePercentageLabel');
        case DiscountType.FIXED:
          if (this.isPercentageDiscount) {
            return this.formatter.percent(this.percentage, {
              maximumFractionDigits: MAXIMUM_FRACTION_DIGITS
            });
          } else {
            return this.formatter.currency(this.amount.amount, currencyCode, {
              maximumFractionDigits: MAXIMUM_FRACTION_DIGITS
            });
          }
        default:
          return null;
      }
    },
    set(_, value) {
      return set(this, '_discountText', value);
    }
  }),
  serialize() {
    // Switch null values to 0s for variable discounts
    const discountType = this.discountType;
    if (discountType === DiscountType.VARIABLE_AMOUNT) {
      this.set('percentage', null);
      this.set('amount', Money.zero());
    } else if (discountType === DiscountType.VARIABLE_PERCENTAGE) {
      this.set('percentage', 0);
      this.set('amount', null);
    } else {
      if (this.isPercentageDiscount) {
        this.set('amount', null);
      } else {
        this.set('percentage', null);
      }
    }
    const json = this._super();
    return json;
  },
  isDirty: computed('hasDirtyFields', 'dirtyType', 'promo.isDirty', 'pricingRule.isDirty', function () {
    return this._super() || this.promo?.isDirty || this.pricingRule?.isDirty;
  }),
  isVariable: computed('_isVariable', 'discountType', {
    get() {
      if (this._isVariable !== undefined) {
        return this._isVariable;
      }
      const discountType = this.discountType;
      return discountType === DiscountType.VARIABLE_AMOUNT || discountType === DiscountType.VARIABLE_PERCENTAGE;
    },
    set(_, value) {
      return set(this, '_isVariable', value);
    }
  }),
  discountRule: computed('pricingRule.excludeProductSet', 'pricingRule.targetProductSet.{exactQuantity,minQuantity}', 'purchaseRule', {
    get() {
      let discountRule;
      if (isPresent(this.pricingRule?.excludeProductSet)) {
        discountRule = DiscountRule.BUY_ONE_GET_ONE;
      } else if (isPresent(this.pricingRule?.targetProductSet?.minQuantity)) {
        discountRule = DiscountRule.MIN_QUANTITY;
      } else if (isPresent(this.pricingRule?.targetProductSet?.exactQuantity)) {
        discountRule = DiscountRule.EXACT_QUANTITY;
      }
      return discountRule;
    },
    set(key, value) {
      this.updateAutoDiscountProps({
        discountRule: value
      });
      return value;
    }
  }),
  // Volume-based discounts have the following structure,
  // where 'fullPriceProductSet' is a productSet containing the items the customer pays the full price on
  //   with an exact quantity attribute set
  // and 'discountProductSet' is a productSet containing the items the customer receives a discount on
  //   with min and max quantity attributes set
  //
  // PricingRule
  //   TargetProductSet
  //     ProductsAll: [fullPriceProductSet, discountProductSet]
  //   ExcludeProductSet: fullPriceProductSet
  fullPriceProductSet: computed('discountRule', 'pricingRule.{excludeProductSet,targetProductSet}', 'purchaseRule', {
    get() {
      if (this.purchaseRule === PurchaseRule.QUANTITY && this.discountRule === DiscountRule.BUY_ONE_GET_ONE) {
        return this.pricingRule?.excludeProductSet;
      }
      return this.pricingRule?.targetProductSet;
    },
    set(key, value) {
      this.updateAutoDiscountProps({
        fullPriceProductSet: value
      });
      return value;
    }
  }),
  discountProductSet: computed('discountRule', 'pricingRule.{excludeProductSet,targetProductSet.productSetsAll}', 'store', {
    get() {
      // The discounted product set is the difference between the targetProductSet and excludeProductSet
      const allProductSets = this.pricingRule?.targetProductSet?.productSetsAll;
      const excludeProductSet = this.pricingRule?.excludeProductSet;

      // Only 1 discount product set is supported in the UI
      const discountProductSet = allProductSets?.find(productSet => productSet !== excludeProductSet);
      return discountProductSet;
    },
    set(key, value) {
      this.updateAutoDiscountProps({
        discountProductSet: value
      });
      return value;
    }
  }),
  customerGroupIdsAny: computed('pricingRule.customerGroupIdsAny', {
    get() {
      return this.pricingRule?.customerGroupIdsAny;
    },
    set(key, value) {
      this.updateAutoDiscountProps({
        customerGroupIdsAny: value
      });
      return value;
    }
  }),
  purchaseRule: computed('customerGroupIdsAny', 'discountRule', 'pricingRule.targetProductSet', {
    get() {
      let purchaseRule = null;
      if (this.customerGroupIdsAny) {
        purchaseRule = PurchaseRule.CUSTOMER;
      } else if (isPresent(this.discountRule)) {
        purchaseRule = PurchaseRule.QUANTITY;
      } else if (isPresent(this.pricingRule?.targetProductSet)) {
        purchaseRule = PurchaseRule.PRODUCTS;
      }
      return purchaseRule;
    },
    set(key, value) {
      this.updateAutoDiscountProps({
        purchaseRule: value
      });
      return value;
    }
  }),
  fullPriceQuantity: computed('_fullPriceQuantity', 'fullPriceProductSet.{exactQuantity,minQuantity}', {
    get() {
      return this.fullPriceProductSet?.minQuantity || this.fullPriceProductSet?.exactQuantity || this._fullPriceQuantity;
    },
    set(key, value) {
      // The quantity field can be reset (set to undefined) if the user fills out
      // the quantity field before choosing items for the fullPriceProductSet.
      // This is because the quantity is derived from fullPriceProductSet.
      // When the quantity is set first, there is no fullPriceProductSet to attach
      // the quantity to. So we save the quantity to prevent it from being reset.
      set(this, '_fullPriceQuantity', value);
      this.updateAutoDiscountProps({
        fullPriceQuantity: value
      });
      return value;
    }
  }),
  discountQuantity: computed('_discountQuantity', 'discountProductSet.maxQuantity', {
    get() {
      return this.discountProductSet?.maxQuantity || this._discountQuantity;
    },
    set(key, value) {
      // The quantity field can be reset (set to undefined) if the user fills out
      // the quantity field before choosing items for the discountProductSet.
      // This is because the quantity is derived from discountProductSet.
      // When the quantity is set first, there is no discountProductSet to attach
      // the quantity to. So we save the quantity to prevent it from being reset.
      set(this, '_discountQuantity', value);
      this.updateAutoDiscountProps({
        discountQuantity: value
      });
      return value;
    }
  }),
  rollbackAttributes() {
    this._super();
    const promo = this.promo;
    const pricingRule = this.pricingRule;
    if (!isNone(promo)) {
      promo.rollbackAttributes();
    }
    if (!isNone(pricingRule)) {
      pricingRule.rollbackAttributes();
    }
  },
  deleteModel() {
    this.set('deleted', true);
  },
  // These 3 functions override multi-unit support functions so we can add/remove the subunits
  // from the multi-unit variations which in turn add/remove them from the item variations themselves
  // These cascade the enabled property when you add a subunit
  addMerchantSubunits(merchantSubunits) {
    if (merchantSubunits.length > 0) {
      this._super(merchantSubunits);
      const promo = this.promo;
      if (!isNone(promo)) {
        promo.addMerchantSubunits(merchantSubunits);
      }
    }
  },
  // This function cascades the enabled property when you remove a subunit
  removeMerchantSubunit(subunitToken) {
    if (this.enabledForUnit(subunitToken)) {
      this._super(subunitToken);
      const promo = this.promo;
      if (!isNone(promo)) {
        promo.removeMerchantSubunit(subunitToken);
      }
    }
  },
  // This function cascades when you change the global enabled property
  updateGloballyEnabledForRelationships(globallyEnabled) {
    const promo = this.promo;
    if (!isNone(promo)) {
      promo.set('globallyEnabled', globallyEnabled);
    }
  },
  updateQuantities(productSet, quantities) {
    if (!productSet) {
      return;
    }
    const {
      exactQuantity = null,
      minQuantity = null,
      maxQuantity = null
    } = quantities;
    productSet.set('exactQuantity', exactQuantity);
    productSet.set('minQuantity', minQuantity);
    productSet.set('maxQuantity', maxQuantity);
  },
  updateValues(values) {
    const {
      excludeStrategy = null,
      excludeProductSet = null,
      adjustedProductSet = null,
      targetProductSet = null,
      customerGroupIdsAny = null,
      discountTargetScope = PricingRule.DiscountTargetScope.LINE_ITEM
    } = values;

    // Initializing excludeStrategy to undefined allows us or merchants to manually fix discounts which were previously broken
    // when we were defaulting to setting an excludeStrategy.
    this.pricingRule.set('excludeStrategy', excludeStrategy);
    this.pricingRule.watchAndSet('excludeProductSet', excludeProductSet);
    this.pricingRule.watchAndSet('adjustedProductSet', adjustedProductSet); // TODO: see if we need to reset this everytime
    this.pricingRule.watchAndSet('targetProductSet', targetProductSet);
    this.pricingRule.set('customerGroupIdsAny', customerGroupIdsAny);
    this.pricingRule.set('discountTargetScope', discountTargetScope);
  },
  updateAutoDiscountProps(props) {
    const {
      fullPriceProductSet = this.fullPriceProductSet ?? this.store.createRecord('product-set'),
      fullPriceQuantity = this.fullPriceQuantity,
      discountProductSet = this.discountProductSet,
      discountQuantity = this.discountQuantity,
      discountRule = this.discountRule,
      purchaseRule = this.purchaseRule,
      customerGroupIdsAny = this.customerGroupIdsAny
    } = props;
    if (purchaseRule === PurchaseRule.QUANTITY) {
      switch (discountRule) {
        case DiscountRule.MIN_QUANTITY:
          this.updateQuantities(fullPriceProductSet, {
            minQuantity: fullPriceQuantity
          });
          this.updateValues({
            targetProductSet: fullPriceProductSet
          });
          break;
        case DiscountRule.EXACT_QUANTITY:
          this.updateQuantities(fullPriceProductSet, {
            exactQuantity: fullPriceQuantity
          });
          this.updateValues({
            targetProductSet: fullPriceProductSet
          });
          break;
        case DiscountRule.BUY_ONE_GET_ONE:
          {
            // Default to new product set here instead of at the top of this
            // function with the rest of the props because we only want
            // to create a new discountProductSet when BOGO is selected.
            const discProductSet = discountProductSet ?? this.store.createRecord('product-set');
            this.updateQuantities(fullPriceProductSet, {
              exactQuantity: fullPriceQuantity
            });
            this.updateQuantities(discProductSet, {
              minQuantity: 1,
              maxQuantity: discountQuantity
            });

            // Only BOGOs set productSetsAll, so check to see if there is an existing
            // targetProductSet for BOGO and use that instead of creating a new one.
            const targetProductSet = this.pricingRule?.targetProductSet?.productSetsAll.length === 2 ? this.pricingRule.targetProductSet : this.store.createRecord('product-set');
            targetProductSet.set('productSetsAll', [fullPriceProductSet, discProductSet]);
            targetProductSet.set('exactQuantity', 1);
            this.updateValues({
              targetProductSet,
              excludeProductSet: fullPriceProductSet,
              excludeStrategy: this.enableExcludeStrategy ? PricingRule.ExcludeStrategy.MOST_EXPENSIVE : undefined
            });
            break;
          }
        default:
          this.updateValues({
            targetProductSet: fullPriceProductSet
          });
          break;
      }
    } else if (purchaseRule === PurchaseRule.CUSTOMER) {
      // The pricing rule for Customer group should be WHOLE_PURCHASE. At one point there was a bug
      // where it was set to LINE_ITEM. We only want to fix this for new pricing rules and leave
      // previously created pricing rules for customer group alone.
      // https://github.com/squareup/dashboard/pull/50531
      //
      // However, there is an edge case with this current fix. Changing purchase rules doesn't
      // create a new pricing rule. For example, if a merchant initially selects the category
      // purchase rule and later changes it to customer, then the pricing rule will remain
      // LINE_ITEM. This will be fixed in the ticket PRICES-7365.
      const discountTargetScope = this.pricingRule.get('isNew') ? PricingRule.DiscountTargetScope.WHOLE_PURCHASE : undefined;

      // Reuse fullPriceProductSet instead of creating a new one.
      const customerProductSet = fullPriceProductSet;
      customerProductSet.clear();
      customerProductSet.set('matchAllProducts', true);
      this.updateValues({
        discountTargetScope,
        targetProductSet: customerProductSet,
        customerGroupIdsAny: customerGroupIdsAny ?? []
      });
    } else {
      this.updateValues({
        targetProductSet: fullPriceProductSet
      });
    }
  }
}).reopenClass({
  DiscountRule,
  DiscountType,
  PurchaseRule,
  ApplicationMethod,
  ModifyTaxBasis,
  MAXIMUM_FRACTION_DIGITS,
  DEFAULT_VARIABLE_DISCOUNT_VALUE,
  sortKey: 'name',
  catalogType() {
    return 'DISCOUNT';
  },
  _modelNameForMigration: 'discount'
});